import React, {FC, useState, useCallback, useMemo} from 'react';
import {useLocalizer} from '@/common/services/localizer';
import {LocationPoint, LocationSuperiorsList} from '@/common/interfaces/location';
import {TabSwitcher} from '@/app/components/desktop/common/TabSwitcher';
import {MapLocationsList} from '../MapLocationsList';
import styles from './MapSettlementsList.css';

type OrderOption = 'largest' | 'alphabetic';
const orderOptions: {value: OrderOption}[] = [{value: 'largest'}, {value: 'alphabetic'}];

interface MapSettlementsListProps {
	location: LocationPoint;
	locationSuperiors: LocationSuperiorsList;
	settlements: LocationPoint[];
}

export const MapSettlementsList: FC<MapSettlementsListProps> = props => {
	const {location, locationSuperiors, settlements} = props;
	const [order, setOrder] = useState<OrderOption>('largest');

	const t = useLocalizer();

	const getOrderTitle = useCallback((option: OrderOption) => t(`sortOptions.${option}`), [t.language]);

	const alphabeticLists = useMemo(() => {
		const map: Record<string, LocationPoint[]> = {};

		const totalList = settlements.slice().sort((a, b) => a.title.localeCompare(b.title));

		for (const item of totalList) {
			const letter = item.title[0].toUpperCase();
			if (!map[letter]) {
				map[letter] = [];
			}

			map[letter].push(item);
		}

		return Object.entries(map).map(([letter, list]) => ({letter, list}));
	}, [settlements]);

	const superiorTitleGc = locationSuperiors.region?.titleGc || locationSuperiors.country?.titleGc;

	// TODO on leave and go from "alphabetic" clean location hash

	return (
		<div>
			<div className={styles.header}>
				<h2 className={styles.title}>
					{t('weatherOfTopSettlements')}{' '}
					{superiorTitleGc ? `${superiorTitleGc}, ${location.title}` : location.titleLc}
				</h2>
				<span className={styles.sortTitle}>{t('sortTitle')}</span>
				<TabSwitcher<OrderOption>
					options={orderOptions}
					getDisplayValue={getOrderTitle}
					value={order}
					onChange={setOrder}
				/>
			</div>

			{order === 'largest' ? (
				<MapLocationsList columns={6} locations={settlements} />
			) : (
				<div>
					<ul className={styles.alphabet}>
						{alphabeticLists.map(({letter}) => (
							<li key={letter} className={styles.alphabetItem}>
								<a className={styles.alphabetLetter} href={`#letter/${letter}`}>
									{letter}
								</a>
							</li>
						))}
					</ul>

					{alphabeticLists.map(({letter, list}) => (
						<div key={letter} className={styles.alphabeticSection}>
							<p id={`letter/${letter}`} className={styles.alphabeticLetterAnchor}>
								{letter}
							</p>
							<div className={styles.alphabeticList}>
								<MapLocationsList columns={5} locations={list} />
							</div>
						</div>
					))}
				</div>
			)}
		</div>
	);
};

MapSettlementsList.displayName = 'MapSettlementsList';
